import React from 'react';
import { User } from '../../resources/types/usersTypes';

interface MatcheeCardProps {
  matchee: User;
  onClick: (handle: string) => void;
  disabled: boolean;
}

const MatcheeCard: React.FC<MatcheeCardProps> = ({ matchee, onClick, disabled }) => {
  const handleClick = () => {
    if (!disabled) {
      onClick(matchee.handle);
    }
  };

  return (
    <div
      className={`
        bg-white rounded-lg p-4 mb-3 shadow-sm border border-sangati-green
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:shadow-md hover:border-sangati-green'}
        transition-all duration-200
      `}
      onClick={handleClick}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onKeyDown={(e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1">
          {/* Phone icon - show if phone_number exists */}
          {matchee.phone_number ? (
            <img
              src="/icons/approved.png"
              alt="Phone verified"
              className="w-8 h-8"
            />
          ) : (<div className="w-8 h-8" ></div>)}
          <span className="text-md font-medium text-gray-900">
            {matchee.handle}
          </span>
        </div>

        <div className="flex items-center flex-shrink-0 ml-3">

          {/* Light mode semi-circle indicator */}
          {matchee.light_mode_active ? (
            <div
              className="w-[25px] h-[49px]"
              title={matchee.light_mode_active ? 'Light mode active' : 'Light mode not active'}
            >
              <svg width="25" height="49" viewBox="0 0 25 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask id="path-1-inside-1_2517_4049" fill="white">
                  <path d="M24.5 49C21.2826 49 18.0967 48.3663 15.1243 47.135C12.1518 45.9038 9.45092 44.0991 7.17588 41.8241C4.90085 39.5491 3.09619 36.8482 1.86495 33.8757C0.633711 30.9033 -1.21157e-06 27.7174 -1.07093e-06 24.5C-9.30293e-07 21.2826 0.633712 18.0967 1.86495 15.1243C3.09619 12.1518 4.90085 9.45092 7.17588 7.17588C9.45092 4.90085 12.1518 3.09619 15.1243 1.86495C18.0967 0.633709 21.2826 -1.49284e-06 24.5 -1.07093e-06L24.5 24.5L24.5 49Z" />
                </mask>
                <path d="M24.5 49C21.2826 49 18.0967 48.3663 15.1243 47.135C12.1518 45.9038 9.45092 44.0991 7.17588 41.8241C4.90085 39.5491 3.09619 36.8482 1.86495 33.8757C0.633711 30.9033 -1.21157e-06 27.7174 -1.07093e-06 24.5C-9.30293e-07 21.2826 0.633712 18.0967 1.86495 15.1243C3.09619 12.1518 4.90085 9.45092 7.17588 7.17588C9.45092 4.90085 12.1518 3.09619 15.1243 1.86495C18.0967 0.633709 21.2826 -1.49284e-06 24.5 -1.07093e-06L24.5 24.5L24.5 49Z" fill="url(#paint0_linear_2517_4049)" stroke="black" stroke-width="2" mask="url(#path-1-inside-1_2517_4049)" />
                <defs>
                  <linearGradient id="paint0_linear_2517_4049" x1="24.5218" y1="-3.56351e-07" x2="24.3064" y2="48.9992" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#8E0017" />
                    <stop offset="1" stop-color="#1D304D" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          ) : (<div className="w-[25px] h-[49px]"></div>)}

          {/* Dark mode semi-circle indicator */}
          {matchee.dark_mode_active ? (
            <div
              className="w-[25px] h-[49px]"
              title={matchee.dark_mode_active ? 'Dark mode active' : 'Dark mode not active'}
            >
              <svg width="25" height="49" viewBox="0 0 25 49" fill="none" xmlns="http://www.w3.org/2000/svg">
                <mask id="path-1-inside-1_2517_4048" fill="white">
                  <path d="M0.5 0C3.71739 0 6.90327 0.633713 9.87574 1.86495C12.8482 3.09619 15.5491 4.90085 17.8241 7.17588C20.0992 9.45092 21.9038 12.1518 23.135 15.1243C24.3663 18.0967 25 21.2826 25 24.5C25 27.7174 24.3663 30.9033 23.135 33.8757C21.9038 36.8482 20.0992 39.5491 17.8241 41.8241C15.5491 44.0992 12.8482 45.9038 9.87574 47.135C6.90326 48.3663 3.71738 49 0.499998 49L0.5 24.5L0.5 0Z" />
                </mask>
                <path d="M0.5 0C3.71739 0 6.90327 0.633713 9.87574 1.86495C12.8482 3.09619 15.5491 4.90085 17.8241 7.17588C20.0992 9.45092 21.9038 12.1518 23.135 15.1243C24.3663 18.0967 25 21.2826 25 24.5C25 27.7174 24.3663 30.9033 23.135 33.8757C21.9038 36.8482 20.0992 39.5491 17.8241 41.8241C15.5491 44.0992 12.8482 45.9038 9.87574 47.135C6.90326 48.3663 3.71738 49 0.499998 49L0.5 24.5L0.5 0Z" fill="url(#paint0_linear_2517_4048)" stroke="black" stroke-width="2" mask="url(#path-1-inside-1_2517_4048)" />
                <defs>
                  <linearGradient id="paint0_linear_2517_4048" x1="0.472582" y1="49" x2="0.743373" y2="0.00119277" gradientUnits="userSpaceOnUse">
                    <stop stop-color="#FBEAEA" />
                    <stop offset="0.7" stop-color="#FFE279" />
                    <stop offset="1" stop-color="#FDAE15" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          ) : (<div className="w-[25px] h-[49px]"></div>)}
        </div>
      </div>
    </div>
  );
};

export default MatcheeCard;
